import { Link } from 'react-router-dom';
import { adoptableCats } from '../../../mocks/adoptableCats';

export default function CompactAdoptionTeaser() {
  const featuredCats = adoptableCats.slice(0, 3);

  return (
    <section className="py-20 bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-10 left-10 w-40 h-40 bg-rose-300/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-48 h-48 bg-orange-300/20 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg mb-4">
            <i className="ri-heart-fill text-rose-500 animate-pulse"></i>
            <span className="text-sm font-bold text-gray-700">Find Your Purr-fect Match</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-4">
            <span className="bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
              Adopt
            </span>{' '}
            a Loving Cat Today
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Give a rescue cat a forever home. Browse verified shelters and meet your new best friend
          </p>
        </div>

        {/* Featured Cats Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {featuredCats.map((cat, index) => (
            <Link
              key={cat.id}
              to={`/adopt/cat?id=${cat.id}`}
              className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Cat Image */}
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Status Badge */}
                <div className="absolute top-4 right-4 px-3 py-1 bg-green-500 text-white text-sm font-bold rounded-full shadow-lg flex items-center gap-1">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  Available
                </div>

                {/* Quick Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <div className="flex items-center gap-3 text-white text-sm">
                    <div className="flex items-center gap-1">
                      <i className="ri-map-pin-fill"></i>
                      <span>{cat.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-calendar-fill"></i>
                      <span>{cat.age}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cat Info */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-2xl font-black text-gray-900 mb-1 group-hover:text-rose-600 transition-colors duration-300">
                      {cat.name}
                    </h3>
                    <p className="text-gray-600 font-medium">{cat.breed}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-heart-fill text-white text-xl"></i>
                  </div>
                </div>

                {/* Personality Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {cat.personality.slice(0, 2).map((trait) => (
                    <span
                      key={trait}
                      className="px-3 py-1 bg-rose-100 text-rose-700 text-xs font-bold rounded-full"
                    >
                      {trait}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <div className="flex items-center gap-2 text-rose-600 font-bold group-hover:gap-4 transition-all duration-300">
                  <span>Meet {cat.name}</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Stats Bar */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-4xl font-black text-rose-600 mb-2">12,000+</div>
              <div className="text-gray-600 font-medium">Cats Adopted</div>
            </div>
            <div>
              <div className="text-4xl font-black text-pink-600 mb-2">500+</div>
              <div className="text-gray-600 font-medium">Partner Shelters</div>
            </div>
            <div>
              <div className="text-4xl font-black text-orange-600 mb-2">98%</div>
              <div className="text-gray-600 font-medium">Success Rate</div>
            </div>
            <div>
              <div className="text-4xl font-black text-rose-600 mb-2">24/7</div>
              <div className="text-gray-600 font-medium">Support Available</div>
            </div>
          </div>
        </div>

        {/* Main CTA */}
        <div className="text-center">
          <Link
            to="/adopt"
            className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-rose-500 to-pink-500 text-white text-xl font-black rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 whitespace-nowrap group"
          >
            <i className="ri-heart-fill text-2xl group-hover:animate-pulse"></i>
            <span>Browse All Adoptable Cats</span>
            <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform duration-300"></i>
          </Link>
          <p className="text-gray-600 mt-6 text-lg">
            Every adoption saves a life. Start your journey today 💕
          </p>
        </div>
      </div>
    </section>
  );
}
